import { Suspense, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UxLoading } from "@imaginelearning/mps-ux-component-library";
import { useAssessments } from "../context/assessment-provider";
import { IAssessment, ISubAssessment } from "../types";
import FormativeInProgress from "./formativeinprogress";
import FormativeNotActive from "./formativenotactive";
import "./formatives-course-map.scss";
import BackButton from "../components/backbutton";
import useFormativeLinkPlayer from "../../hooks/linkplayer/useFormativeLinkPlayer";
import ProjectLinkPlayer from "../../components/project-link-player/project-link-player";
import UnauthorizedQuizModal from "../../components/assignments/course-map-components/unauthorized-quiz-modal";
import AssessmentLoading from "../components/assessment-loading";

function AssessmentFormativesMap() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { loading, assessments, reloadData } = useAssessments();
  const [currentAssessment, setCurrentAssessment] = useState<IAssessment | null>(null);
  const [nextSubAssessment, setNextSubAssessment] = useState<ISubAssessment | undefined>();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [activeLesson, setActiveLesson] = useState<ISubAssessment | null>(null);
  const [alpState, setAlpState] = useState({ state: "", assignmentID: "" });

  const reloadFormatives = useCallback(() => {
    setActiveLesson(null);
    reloadData();
  }, [setActiveLesson, reloadData]);

  const showError = useCallback(() => {
    setShowErrorDialog(true);
  }, [setShowErrorDialog]);

  const { createAttempt, readyToLaunch, launchProps } = useFormativeLinkPlayer(reloadFormatives, showError, alpState);

  useEffect(() => {
    setAlpState({ state: currentAssessment?.state ?? "", assignmentID: id });
  }, [currentAssessment, id]);

  useEffect(() => {
    if (assessments && navigate) {
      const assessment = assessments.find((assessment) => assessment.id === id);
      if (assessment) {
        setCurrentAssessment(assessment);
      } else {
        // if assessment not found, navigate back to assessments
        navigate("/assessments");
      }
    }
  }, [assessments, id, navigate]);

  useEffect(() => {
    let next = currentAssessment?.assessments?.find((assessment) => assessment.status === "InProgress");
    if (!next) {
      // get first assessment with NotStarted
      next = currentAssessment?.assessments?.find((assessment) => assessment.status === "NotStarted");
    }
    setNextSubAssessment(next);
  }, [currentAssessment]);

  useEffect(() => {
    if (activeLesson) {
      const attemptCreation = async () => {
        try {
          await createAttempt(activeLesson.pathID, {
            introMode: "None",
            outroMode: "None"
          });
        } catch (error) {
          console.error("Error creating attempt:", error);
        }
      };

      attemptCreation();
    }
  }, [activeLesson, createAttempt]);

  const returnToMap = () => {
    navigate("/assessments");
  };

  if (!currentAssessment || loading) {
    return <AssessmentLoading />;
  }

  if (showErrorDialog) {
    return <UnauthorizedQuizModal showErrorDialog={showErrorDialog} setShowErrorDialog={setShowErrorDialog} />;
  }

  if (readyToLaunch && launchProps) {
    return (
      <div className="fullscreen-container">
        <Suspense fallback={<UxLoading />}>
          <ProjectLinkPlayer {...launchProps} />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="formatives-course-map">
      <BackButton onClick={returnToMap} />
      {currentAssessment?.assessments?.map((assessment) => {
        if (assessment.pathID === nextSubAssessment?.pathID) {
          return (
            <FormativeInProgress
              key={assessment.pathID}
              assessment={assessment}
              onStart={() => setActiveLesson(assessment)}
            />
          );
        }

        return <FormativeNotActive key={assessment.pathID} assessment={assessment} />;
      })}
    </div>
  );
}

export default AssessmentFormativesMap;
